import React, { useEffect, useState } from "react";

const GoogleMapsProvider = ({ apiKey, children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Fully loaded with places library
    if (window.google?.maps?.places) {
      setIsLoaded(true);
      return;
    }

    const key = apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!key) {
      setLoadError(
        "Google Maps API key not found. Set REACT_APP_GOOGLE_MAPS_API_KEY or pass apiKey prop.",
      );
      return;
    }

    // If Google Maps core is loaded but without places, load places via the
    // same API script URL (Google deduplicates and just adds the library).
    // If nothing is loaded yet, load everything from scratch.
    const existing = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );

    if (window.google?.maps && !window.google.maps.places) {
      // Core loaded (e.g., by react-leaflet-google-layer) but places missing.
      // Google's JS API supports a second script tag to add libraries.
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry`;
      script.async = true;
      script.onload = () => setIsLoaded(true);
      script.onerror = () =>
        setLoadError("Failed to load Google Maps Places library.");
      document.head.appendChild(script);
      return;
    }

    if (existing) {
      // Script tag exists but hasn't finished loading yet
      const onLoad = () => setIsLoaded(true);
      const onError = () => setLoadError("Failed to load Google Maps API.");
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
    }

    // Nothing loaded — load from scratch
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () =>
      setLoadError(
        "Failed to load Google Maps API. Please check your API key and internet connection.",
      );
    document.head.appendChild(script);
  }, [apiKey]);

  if (loadError) {
    return (
      <div className="max-w-md mx-auto p-4 bg-red-50 border border-red-200 rounded-md">
        <h2 className="text-lg font-semibold text-red-700">
          Error Loading Maps
        </h2>
        <p className="text-red-600 mt-2">{loadError}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="max-w-md mx-auto p-4 flex flex-col items-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-700">Loading Google Maps...</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default GoogleMapsProvider;
